import type { DemoListItem } from 'services/demosApi'

/**
 * The text an AE pastes into their own mail client.
 *
 * This is deliberately NOT an email send. Nothing in the demo code sends mail,
 * and inventing a send here would mean cold-emailing prospects from Vera
 * infrastructure with no unsubscribe, no suppression list and no deliverability
 * setup. Copy-to-clipboard keeps the send in the AE's own outbox, where their
 * domain reputation and their signature already live.
 *
 * It is written to become the body of a real templated invite later (the point
 * at which `sent_at` starts being written and the "Sent" column can come back),
 * so it is a pure function of the row with no React or clipboard concerns.
 *
 * ── Why the copy says what it says ──────────────────────────────────────────
 *
 * This lands on someone with no relationship to Vera, looking at their own
 * brand's imagery on a stranger's infrastructure. The three lines that exist to
 * defuse that are load-bearing, not padding:
 *   - "built from your public site" — says where the content came from, before
 *     they wonder.
 *   - "not connected to your systems" — the first thing a technical reader
 *     assumes and the thing that would alarm them.
 *   - "we'll take it down" — makes removal a reply rather than a complaint.
 * Removing them makes this read as a breach notice instead of an offer.
 */
export const demoInviteText = (row: DemoListItem): string => {
  // VRG-723 — the OUTREACH certificate's link when there is one (the certificate the prospect is
  // shown first), else the demo's home. Both come from the API, on the customer app host for this
  // environment; nothing here builds a host.
  const url = row.outreachCertificate?.url ?? row.demoUrl
  const greeting = row.prospectContact ? `Hi ${row.prospectContact.split('@')[0]},` : 'Hi,'

  return [
    greeting,
    '',
    `We built ${row.companyName} a working demo of Vera, using your own products, so you can see exactly what your customers would see.`,
    '',
    `It's live here: ${url}`,
    '',
    "Every product in it is a real Vera certificate. Open one and you get the same page a customer gets when they tap the tag in the physical product.",
    '',
    "It's built from your public site, it isn't connected to any of your systems, and it isn't indexed by search engines. Reply and we'll take it down.",
  ].join('\n')
}
